import type { ComponentType, SVGProps } from 'react';

import { Badge } from '@pesto/ui/components/Badge';
import {
  CheckCircle2,
  CircleSlash,
  CreditCard,
  RefreshCw,
  ShieldAlert,
  ShieldX,
  Smartphone,
  XCircle,
} from 'lucide-react';

import type { CardKind } from '../types/Cards.types';

import { deriveCardKind } from '../utils/deriveCardKind';

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning';

interface KindMeta {
  label: string;
  variant: BadgeVariant;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}

const KIND_META: Record<CardKind, KindMeta> = {
  'physical-active': { label: 'Physical · Active', variant: 'success', icon: CheckCircle2 },
  'digital-active': { label: 'Digital · Active', variant: 'default', icon: Smartphone },
  'not-activated': { label: 'Not activated', variant: 'secondary', icon: CreditCard },
  blocked: { label: 'Blocked', variant: 'warning', icon: ShieldAlert },
  closed: { label: 'Closed', variant: 'outline', icon: CircleSlash },
  lost: { label: 'Lost', variant: 'destructive', icon: ShieldX },
  stolen: { label: 'Stolen', variant: 'destructive', icon: ShieldX },
  replaced: { label: 'Replaced', variant: 'outline', icon: RefreshCw },
  unknown: { label: 'Unknown', variant: 'outline', icon: XCircle },
};

interface CardStatusBadgeProps {
  status: string | null;
  physicalActivatedAt?: string | null;
  className?: string;
}

export const CardStatusBadge = ({ status, physicalActivatedAt = null, className }: CardStatusBadgeProps) => {
  const kind = deriveCardKind({ status, physicalActivatedAt });
  const meta = KIND_META[kind];
  const Icon = meta.icon;

  return (
    <Badge variant={meta.variant} className={className}>
      <Icon className="mr-1 h-3 w-3" />
      {meta.label}
    </Badge>
  );
};
